import React, { useState } from 'react';
import { darken } from '@mui/system';

import { BasicProfile, ProfileLine, WebJointSegment } from '@paragon/contract-library';
import { RaycastOrder, RenderOrder } from '@paragon/three-shared';

import { useAppDispatch, useAppSelector } from '../../components/hooks';
import { calculateWebJointSegmentGeometry } from '../../library/profiles/profileManipulation';
import { getTransformationPropsForSegment } from '../../library/utilities/updateMatrixFromSegment';
import { handleStandardSceneItemRightClick, useClickItem } from '../../store/actions/sceneInteractionActions';
import { clearHoverItemIfActive, ensureHoverItemIsActive } from '../../store/actions/viewportActions';
import { getSingleSelectedItemEntryOrNull } from '../../store/selectors/getSelectedItemSelector';
import { WebJointVisual } from './WebJointVisual';

interface Props {
  readonly profile: BasicProfile;
  readonly profileLine: ProfileLine;
  readonly webJointSegment: WebJointSegment;
  readonly registerForRaycasting: boolean;
  readonly shouldShowWebbingVisuals: boolean;
}

export const WEB_COLOR = '#ee10ee';
export const WEB_HOVER_COLOR = darken(WEB_COLOR, 0.35);
export const WEB_SELECTED_COLOR = '#ff0022'; // '#ee1088';

export const WebJointSegmentVisual = ({
  profile,
  profileLine,
  webJointSegment,
  registerForRaycasting,
  shouldShowWebbingVisuals,
}: Props) => {
  const dispatch = useAppDispatch();

  const [hovered, setHovered] = useState(false);

  const selectedEntry = useAppSelector(getSingleSelectedItemEntryOrNull);
  const selected = selectedEntry?.type === 'webJointSegment' && selectedEntry.item.guid === webJointSegment.guid;

  const { basePoint, endPoint } = calculateWebJointSegmentGeometry(profile, profileLine, webJointSegment).to3D();

  const joints = shouldShowWebbingVisuals
    ? webJointSegment.floatingJoints.map((floatingJoint) => (
        <WebJointVisual
          key={floatingJoint.guid}
          segmentGeometry={calculateWebJointSegmentGeometry(profile, profileLine, webJointSegment)}
          webJoint={floatingJoint}
          jointType="floating"
          registerForRaycasting={registerForRaycasting}
        />
      ))
    : null;

  const interactionHandle = { type: 'webJointSegment', guid: webJointSegment.guid, origin: 'fiber' } as const;
  const onClick = useClickItem({ type: 'webJointSegment', item: webJointSegment });

  return (
    <group>
      <object3D>
        <mesh
          renderOrder={RenderOrder.WebJointSegmentVisual}
          {...getTransformationPropsForSegment(basePoint, endPoint)}
        >
          <cylinderGeometry args={[0.25, 0.25, 1, 8, 1, false]} />
          <meshBasicMaterial
            color={selected ? WEB_SELECTED_COLOR : hovered ? WEB_HOVER_COLOR : WEB_COLOR}
            transparent
            depthTest={false}
            depthWrite={false}
            opacity={1}
          />
        </mesh>

        <mesh
          renderOrder={RenderOrder.WebJointSegmentVisual}
          {...getTransformationPropsForSegment(basePoint, endPoint)}
          onClick={registerForRaycasting ? onClick : undefined}
          onContextMenu={async (event) => await dispatch(handleStandardSceneItemRightClick(event, interactionHandle))}
          onPointerOver={(event) => {
            event.stopPropagation();

            setHovered(true);
            dispatch(ensureHoverItemIsActive(interactionHandle));
          }}
          onPointerOut={(event) => {
            event.stopPropagation();

            setHovered(false);
            dispatch(clearHoverItemIfActive(interactionHandle));
          }}
          userData-raycastOrder={RaycastOrder.ProfileWebJointSegmentVisual}
        >
          <cylinderGeometry args={[1.5, 1.5, 1, 8, 1, false]} />
          <meshBasicMaterial color={0xee10ee} transparent depthTest={false} depthWrite={false} opacity={0} />
        </mesh>
      </object3D>
      {joints}
    </group>
  );
};
